import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

type BasicDependentInfo = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  relationship?: string | null;
};

type DeletionCoverage = {
  planId: number;
  planName: string;
  planType: string | null;
  coverageTier: string | null;
  effectiveDate: string | null;
};

type DeletionSummary = {
  dependent: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    relationship: string | null;
  };
  coverage: DeletionCoverage[];
  canDelete: boolean;
  blockingReason?: string;
};

interface DependentDeletionDialogProps {
  dependent: BasicDependentInfo | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  onNavigateToEnrollments: () => void;
  isProcessing: boolean;
  errorMessage?: string | null;
}

function buildSummaryEndpoint(dependent: BasicDependentInfo | null): string {
  if (dependent && typeof dependent.id === "number") {
    return `/api/dependents/${dependent.id}/deletion-summary`;
  }
  return "dependent-deletion-summary";
}

function getDependentDisplayName(dependent: BasicDependentInfo | null): string {
  if (!dependent) {
    return "this dependent";
  }

  const first = dependent.firstName?.trim() ?? "";
  const last = dependent.lastName?.trim() ?? "";
  const combined = `${first} ${last}`.trim();

  if (combined.length > 0) {
    return combined;
  }

  if (dependent.relationship) {
    return `this ${dependent.relationship.toLowerCase()}`;
  }

  return "this dependent";
}

function describeCoverageItem(item: DeletionCoverage): string {
  const details: string[] = [];
  details.push(item.planName);

  if (item.planType) {
    details.push(`(${item.planType})`);
  }

  if (item.coverageTier) {
    details.push(`Tier: ${item.coverageTier}`);
  }

  if (item.effectiveDate) {
    details.push(`Effective ${item.effectiveDate}`);
  }

  return details.join(" ");
}

export function DependentDeletionDialog({
  dependent,
  isOpen,
  onClose,
  onConfirm,
  onNavigateToEnrollments,
  isProcessing,
  errorMessage,
}: DependentDeletionDialogProps) {
  const summaryEndpoint = useMemo(
    () => buildSummaryEndpoint(dependent),
    [dependent]
  );

  const { data: summary, isFetching, isError, refetch } = useQuery<DeletionSummary>({
    queryKey: [summaryEndpoint],
    enabled: isOpen && Boolean(dependent?.id),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [acknowledgementTime, setAcknowledgementTime] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      setAcknowledgementTime(new Date().toLocaleString());
    } else {
      setAcknowledgementTime("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dependent?.id) {
      refetch();
    }
  }, [isOpen, dependent?.id, refetch]);

  const displayName = useMemo(
    () => getDependentDisplayName(dependent),
    [dependent]
  );

  const coverageItems = useMemo(
    () => summary?.coverage ?? [],
    [summary]
  );

  const coverageList = useMemo(() => {
    if (coverageItems.length === 0) {
      return null;
    }

    return coverageItems.map((item) => (
      <li
        key={`${item.planId}-${item.coverageTier ?? "coverage"}-${item.effectiveDate ?? "current"}`}
        className="rounded-lg border border-muted px-3 py-2 text-sm"
      >
        <div className="font-medium text-foreground">
          {describeCoverageItem(item)}
        </div>
      </li>
    ));
  }, [coverageItems]);

  const canDelete = summary?.canDelete ?? false;
  const isBusy = isProcessing || isFetching;
  const confirmDisabled = isBusy || !canDelete;

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleNavigateToEnrollments = useCallback(() => {
    onNavigateToEnrollments();
    onClose();
  }, [onNavigateToEnrollments, onClose]);

  const handleConfirm = useCallback(async () => {
    if (confirmDisabled) {
      return;
    }

    try {
      await onConfirm();
    } catch {
      // Let the parent surface any errors; keep the dialog open
    }
  }, [confirmDisabled, onConfirm]);

  const alertLines = useMemo(() => {
    if (summary && summary.blockingReason && !canDelete) {
      return summary.blockingReason.split("\n");
    }

    const timeLabel = acknowledgementTime || "pending";
    return [
      `This removal will be recorded with a date and time stamp (${timeLabel}).`,
    ];
  }, [summary, canDelete, acknowledgementTime]);

  const alertContent = useMemo(
    () =>
      alertLines.map((line) => (
        <div key={line} className="leading-6">
          {line}
        </div>
      )),
    [alertLines]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Delete Dependent</DialogTitle>
          <DialogDescription>
            Review the information below before removing {displayName} from your profile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert variant={canDelete ? "default" : "destructive"}>
            <AlertTriangle className="mr-2 h-4 w-4 shrink-0" />
            <AlertDescription>{alertContent}</AlertDescription>
          </Alert>

          {isError && (
            <Alert variant="destructive">
              <AlertDescription>
                We were unable to load the coverage details for this dependent. Please try again in a moment.
              </AlertDescription>
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {renderDependentDetails(summary, displayName)}

          {coverageList && coverageItems.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground">
                Active Coverage
              </p>
              <ul className="space-y-2">{coverageList}</ul>
              <Button
                variant="outline"
                onClick={handleNavigateToEnrollments}
                className="w-full justify-center"
              >
                View enrollments to manage coverage
              </Button>
            </div>
          )}
        </div>

        <div className="mt-6 flex w-full justify-end gap-3">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={confirmDisabled}
            onClick={handleConfirm}
          >
            {isBusy ? "Deleting..." : "Delete Dependent"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderDependentDetails(
  summary: DeletionSummary | undefined,
  displayName: string
) {
  if (!summary) {
    return (
      <div className="rounded-lg border border-dashed border-muted bg-muted/10 px-3 py-4 text-sm text-muted-foreground">
        Loading dependent details…
      </div>
    );
  }

  const relationshipLabel = summary.dependent.relationship
    ? summary.dependent.relationship
    : "Dependent";

  return (
    <div className="rounded-lg border border-muted px-3 py-4">
      <p className="text-sm text-muted-foreground">Dependent</p>
      <p className="text-lg font-semibold text-foreground">{displayName}</p>
      <div className="mt-2">
        <Badge variant="outline" className="capitalize">
          {relationshipLabel}
        </Badge>
      </div>
    </div>
  );
}
